

<script setup lang="js">
/*
	Toaster component for use with vue-toastification

	Supporting Class: models/utils/Toaster.js

	Library: https://github.com/Maronato/vue-toastification
	Sandbox: https://vue-toastification.maronato.dev/
 */

import { computed } from 'vue'


const props = defineProps({
	/**
	 * What type of toast to show
	 * error, warning, success, high-warning etc. (see Toaster.js)
	 */
	type: { type: String},

	/**
	 * What kind of toast is this, a toast (shows at the bottom and times out)
	 * or a banner shown at the top that must be manually dismissed.
	 * "toast" or "banner"
	 */
	toastType: { type: String},

	/**
	 * Title to show
	 */
	title: { type: String },

	/**
	 * Toast message to show
	 */
	text: { type: String }
});

/**
 * Determines the title to show based on the type of toast
 * @vue-computed {string}
 */
const bannerTitle = computed(() => {
	switch (props.type) {
		case 'success':
			return "Success";
		case 'error':
			return "Error";
		case 'warning':
			return "Warning";
		case 'high-warning':
			return "High Level Warning";
		case 'info':
			return "Information";
	}

	return "";
})

/**
 * Shows the hidden error details if the user Alt+Shift+Clicks a banner
 * @param e
 */
function showErrorDetails(e)
{
	if (e.altKey && e.shiftKey)
	{
		const elements = document.getElementsByClassName('hidden-error');

		// 2. Loop and remove the class
		elements.forEach(el => {
			el.classList.remove('hidden-error');
		});
	}
}

</script>

<style>
/*
	When setting CSS, remember that priority increases with specificity, so don't forget to select the existing classes as well
	See link above for more details
*/

.Vue-Toastification__container
{
	z-index: 9999999;
}

.Vue-Toastification__toast
{
	padding: 1.1rem 1rem;
	/*border-inline-start-width: 10px;*/
	min-width:500px;
	align-items: start;/* Keeps the close button at the top of the toast, looked weird being in the center*/
}

.Vue-Toastification__toast.banner
{
	border-inline-start-width: 10px;
}

.Vue-Toastification__toast--error {
	background-color: #FBE0D0;
	color: #8B0A03;
	border-color: #B50909;
}

.Vue-Toastification__close-button
{
	opacity: 0.5;
}

.Vue-Toastification__toast--error .Vue-Toastification__close-button{
	color: #8B0A03;
}

.Vue-Toastification__toast--success {
	background-color: #C9FBEB;
	color: #0C4E29;
	border-color: #00A871;
}

.Vue-Toastification__toast--success .Vue-Toastification__close-button{
	color: #0C4E29;
}

.Vue-Toastification__toast--info {
	background-color: #DEE5FF;
	color: #222FBF;
	border-color: #4866FF;
}

.Vue-Toastification__toast--info .Vue-Toastification__close-button{
	color: #222FBF;
}

.Vue-Toastification__toast--warning {
	background-color: #FEF0C8;
	color: #7A591A;
	border-color: #FBBF24;
}

.Vue-Toastification__toast--warning .Vue-Toastification__close-button{
	color: #7A591A;
}

.Vue-Toastification__toast--warning.high-warning {
	background-color: #FFE2D1;
	color: #782312;
	border-color: #EF5E25;
}

.Vue-Toastification__toast--warning .high-warning .Vue-Toastification__close-button{
	color: #EF5E25;
}

.hidden-error
{
	display:none;
}

.toast-message
{
	max-height:46.375rem;
	max-width:30.25rem; /* prevent very long urls (end point table requests making text go outside toast*/
	@apply text-base overflow-y-auto;
}

</style>

<template>
	<div class="container flex flex-row items-center" @click="showErrorDetails">
		<div class="mr-4">
			<svg v-if="props.type === 'error'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-error"/>
				<path d="M18 28C23.5228 28 28 23.5228 28 18C28 12.4772 23.5228 8 18 8C12.4772 8 8 12.4772 8 18C8 23.5228 12.4772 28 18 28Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M21 15L15 21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M15 15L21 21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

			<svg v-if="props.type === 'success'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-success"/>
				<path d="M26 12L15 23L10 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

			<svg v-if="props.type === 'warning'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-lo-warning-on-color"/>
				<path d="M16.2888 9.85996L7.8188 24C7.64417 24.3024 7.55177 24.6453 7.55079 24.9945C7.54981 25.3437 7.64029 25.6871 7.81323 25.9905C7.98616 26.2939 8.23553 26.5467 8.53651 26.7238C8.83749 26.9009 9.1796 26.9961 9.5288 27H26.4688C26.818 26.9961 27.1601 26.9009 27.4611 26.7238C27.7621 26.5467 28.0114 26.2939 28.1844 25.9905C28.3573 25.6871 28.4478 25.3437 28.4468 24.9945C28.4458 24.6453 28.3534 24.3024 28.1788 24L19.7088 9.85996C19.5305 9.56607 19.2795 9.32308 18.98 9.15444C18.6805 8.98581 18.3425 8.89722 17.9988 8.89722C17.6551 8.89722 17.3171 8.98581 17.0176 9.15444C16.7181 9.32308 16.4671 9.56607 16.2888 9.85996Z" class="stroke-icon-lo-warning" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 15V19" class="stroke-icon-lo-warning" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 23H18.01" class="stroke-icon-lo-warning" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

			<svg v-if="props.type === 'high-warning'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-hi-warning"/>
				<path d="M13.86 8H22.14L28 13.86V22.14L22.14 28H13.86L8 22.14V13.86L13.86 8Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 14V18" class="stroke-icon-primary-on-color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 22H18.01" class="stroke-icon-primary-on-color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

			<!-- Info have different icons for some reason -->
			<svg v-if="props.type === 'info' && props.toastType === 'toast'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-information" />
				<path d="M18 28C23.5228 28 28 23.5228 28 18C28 12.4772 23.5228 8 18 8C12.4772 8 8 12.4772 8 18C8 23.5228 12.4772 28 18 28Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 22V18" class="stroke-icon-primary-on-color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M18 14H18.01" class="stroke-icon-primary-on-color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

			<svg v-if="props.type === 'info' && props.toastType === 'banner'" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
				<rect width="36" height="36" rx="18" class="fill-icon-information"/>
				<path d="M19 8L9 20H18L17 28L27 16H18L19 8Z" class="stroke-icon-primary-on-color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>

		</div>

		<div>
			<div v-show="bannerTitle.length > 0" v-html="bannerTitle" class="font-bold text-xl" ></div>
			<div v-html="props.text" class="toast-message"></div>
		</div>

	</div>
</template>